/**
 * RevenueFlows AI - Master JavaScript
 * Version: 1.0.0
 * Quantum Shell Architecture
 * 
 * TABLE OF CONTENTS:
 * 1. DOM Ready Handler
 * 2. Scroll Reveal Animations
 * 3. Navigation Toggle
 * 4. FAQ Accordion
 * 5. Revenue Calculator
 * 6. Smooth Scroll Enhancement
 */

(function () {
    'use strict';

    // ==========================================================================
    // 1. DOM READY HANDLER
    // ==========================================================================

    document.addEventListener('DOMContentLoaded', function () {
        initScrollReveal();
        initNavigation();
        initFAQ();
        initCalculator();
        initSmoothScroll();
    });

    // ==========================================================================
    // 2. SCROLL REVEAL ANIMATIONS
    // ==========================================================================

    function initScrollReveal() {
        const revealElements = document.querySelectorAll('[data-reveal]');

        if (revealElements.length === 0) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('active');
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        });

        revealElements.forEach(el => observer.observe(el));
    }

    // ==========================================================================
    // 3. NAVIGATION TOGGLE
    // ==========================================================================

    function initNavigation() {
        const hamburger = document.getElementById('hamburger');
        const navMenu = document.getElementById('navMenu');

        if (!hamburger || !navMenu) return;

        // Clone to remove existing listeners
        const newHamburger = hamburger.cloneNode(true);
        hamburger.parentNode.replaceChild(newHamburger, hamburger);

        // Toggle menu on hamburger click
        newHamburger.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            navMenu.classList.toggle('active');
            newHamburger.classList.toggle('active');
        });

        // Close menu when clicking a navigation link
        const navLinks = navMenu.querySelectorAll('a');
        navLinks.forEach(link => {
            link.addEventListener('click', function () {
                navMenu.classList.remove('active');
                newHamburger.classList.remove('active');
            });
        });

        // Close menu when clicking outside
        document.addEventListener('click', function (e) {
            if (!newHamburger.contains(e.target) && !navMenu.contains(e.target)) {
                navMenu.classList.remove('active');
                newHamburger.classList.remove('active');
            }
        });

        // Close menu on escape key
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                navMenu.classList.remove('active');
                newHamburger.classList.remove('active');
            }
        });
    }

    // ==========================================================================
    // 4. FAQ ACCORDION
    // ==========================================================================

    function initFAQ() {
        const faqTriggers = document.querySelectorAll('.faq-trigger');

        if (faqTriggers.length === 0) return;

        faqTriggers.forEach(trigger => {
            trigger.addEventListener('click', function () {
                const item = trigger.parentElement;
                const wasActive = item.classList.contains('active');

                // Close all other FAQ items
                document.querySelectorAll('.faq-item').forEach(faqItem => {
                    faqItem.classList.remove('active');
                });

                // Toggle current item (open if it wasn't active)
                if (!wasActive) {
                    item.classList.add('active');
                }
            });
        });
    }

    // ==========================================================================
    // 5. REVENUE CALCULATOR
    // ==========================================================================

    function initCalculator() {
        const trafficInput = document.getElementById('traffic');
        const aovInput = document.getElementById('aov');
        const crInput = document.getElementById('cr');
        const crGrowInput = document.getElementById('cr-grow');
        const aovGrowInput = document.getElementById('aov-grow');

        // Exit if calculator elements don't exist on this page
        if (!trafficInput || !aovInput || !crInput || !crGrowInput || !aovGrowInput) return;

        function updateCalculator() {
            const traffic = parseInt(trafficInput.value);
            const aov = parseInt(aovInput.value);
            const cr = parseFloat(crInput.value) / 100;
            const crGrow = parseFloat(crGrowInput.value) / 100;
            const aovGrow = parseFloat(aovGrowInput.value) / 100;

            // Update display labels
            updateLabel('val-traffic', traffic.toLocaleString());
            updateLabel('val-aov', '$' + aov);
            updateLabel('val-cr', crInput.value + '%');
            updateLabel('val-cr-grow', '+' + crGrowInput.value + '%');
            updateLabel('val-aov-grow', '+' + aovGrowInput.value + '%');

            // Calculate values
            const currentRevenue = traffic * cr * aov;
            const newCR = cr * (1 + crGrow);
            const newAOV = aov * (1 + aovGrow);
            const newRevenue = traffic * newCR * newAOV;
            const newRPV = newCR * newAOV;

            // Update results
            updateLabel('current-rev', '$' + Math.floor(currentRevenue).toLocaleString());
            updateLabel('new-rev', '$' + Math.floor(newRevenue).toLocaleString());
            updateLabel('result-rpv', '$' + newRPV.toFixed(2));
        }

        function updateLabel(id, value) {
            const element = document.getElementById(id);
            if (element) {
                element.innerText = value;
            }
        }

        // Attach event listeners to all inputs
        [trafficInput, aovInput, crInput, crGrowInput, aovGrowInput].forEach(input => {
            input.addEventListener('input', updateCalculator);
        });

        // Initialize calculator with default values
        updateCalculator();
    }

    // ==========================================================================
    // 6. SMOOTH SCROLL ENHANCEMENT
    // ==========================================================================

    function initSmoothScroll() {
        // Handle anchor links with smooth scrolling
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function (e) {
                const targetId = this.getAttribute('href');

                // Don't interfere with empty or "#" links
                if (targetId === '#' || targetId.length <= 1) return;

                const targetElement = document.querySelector(targetId);

                if (targetElement) {
                    e.preventDefault();

                    const headerHeight = parseInt(
                        getComputedStyle(document.documentElement)
                            .getPropertyValue('--header-height')
                    ) || 80;

                    const targetPosition = targetElement.getBoundingClientRect().top +
                        window.pageYOffset -
                        headerHeight;

                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }
            });
        });
    }

    // ==========================================================================
    // UTILITY FUNCTIONS (Exposed globally for page-specific use)
    // ==========================================================================

    window.RevenueFlows = {
        // Format number with commas
        formatNumber: function (num) {
            return num.toLocaleString();
        },

        // Format currency
        formatCurrency: function (num) {
            return '$' + num.toLocaleString();
        },

        // Scroll to element by ID
        scrollTo: function (elementId) {
            const element = document.getElementById(elementId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },

        // Show/hide element
        toggle: function (elementId) {
            const element = document.getElementById(elementId);
            if (element) {
                element.classList.toggle('hidden');
            }
        }
    };

})();
